export type ColorMode = "light" | "dark";

export const COLOR_MODE_STORAGE_KEY = "hubColorMode";

export const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)";

/** Dark is still being rolled out surface by surface. */
export const isDarkModeEnabled = () => process.env.NEXT_PUBLIC_ENABLE_DARK_MODE === "true";

/** An explicit choice outranks the OS; otherwise the OS decides. */
export const resolveColorMode = (stored: string | null, prefersDark: boolean): ColorMode => {
  if (stored === "light" || stored === "dark") {
    return stored;
  }

  return prefersDark ? "dark" : "light";
};

/**
 * Runs before first paint so a visitor never sees the wrong mode flash. It has
 * to be a blocking inline script: next/script's beforeInteractive is still
 * hydration-managed and loses the race, and _app.tsx renders nothing until
 * `canRender` flips, so React cannot colour the first frame. globals.css
 * paints it from the html[data-mode] rules.
 *
 * Deliberately not a cookie: this app keeps UI preferences in localStorage
 * (see SidebarContext), and a cookie would split where preferences live.
 *
 * Mirrors resolveColorMode above; colorModeScript.test.ts runs this string and
 * asserts the two agree. Kept in this React-free module so _document does not
 * import a component.
 */
export const COLOR_MODE_BOOT_SCRIPT =
  `try{var m=null;` +
  // Guarded separately so blocked storage still leaves the OS check to run.
  `try{m=localStorage.getItem(${JSON.stringify(COLOR_MODE_STORAGE_KEY)})}catch(e){}` +
  `if(m!=="light"&&m!=="dark"){m=matchMedia(${JSON.stringify(DARK_SCHEME_QUERY)}).matches?"dark":"light"}` +
  `document.documentElement.dataset.mode=m}catch(e){}`;
